import { z } from "zod";
/* The lab analyte catalog: identity, self-report eligibility, and reference
 * ranges for the panel Ferrivia orders and clients self-report against.
 *
 * Reference ranges are look-up metadata, never a value stored on a result row
 * and never a numeric pair a caller could compare a measurement to (root
 * CLAUDE.md "The software does not interpret"; REQ-MDR-002). `refText` is a
 * display string; there is no refLow/refHigh anywhere in this file.
 */
// Bump whenever an analyte code is added or removed, or a unit changes — a
// stored lab_result_analytes row snapshots this version so it stays
// interpretable even if the catalog changes later.
export const LAB_ANALYTE_CATALOG_VERSION = 2;
// Analyte identity: label, unit, Finnish description. No range here — see
// LAB_REFERENCE_RANGES below, which varies by lab and sex independently of
// this identity.
export const LAB_ANALYTES = [
    {
        code: "ferritin",
        label: "Ferritiini",
        unit: "µg/L",
        description: "Ferritiini on kehon tärkein raudan varastoproteiini. Verestä mitattu ferritiiniarvo kertoo, paljonko rautaa kehossa on varastoituneena.",
    },
    {
        code: "crp",
        label: "Herkkä CRP",
        unit: "mg/L",
        description: "Herkkä CRP (C-reaktiivinen proteiini) on maksan tuottama proteiini, jonka pitoisuus veressä nousee elimistön tulehdustilassa.",
    },
    {
        code: "hemoglobin",
        label: "Hemoglobiini",
        unit: "g/L",
        description: "Hemoglobiini on punasolujen sisältämä happea kuljettava proteiini. Se kertoo veren kyvystä kuljettaa happea kudoksiin.",
    },
    {
        code: "eryt",
        label: "Punasolujen määrä",
        // Ported as written from the prototype. The prototype's unit (10⁹/L) does
        // not match its own range (3.90–5.20, a 10¹²/L range) — flagged for Mari
        // in the content review below, not silently "fixed" here.
        unit: "10⁹/L",
        description: "Punasolujen eli erytrosyyttien määrä kertoo, kuinka monta happea kuljettavaa punasolua verinäytteessä on.",
    },
    {
        code: "hkr",
        label: "Hematokriitti",
        unit: "%",
        description: "Hematokriitti kertoo, kuinka suuri osuus verestä on tilavuudeltaan punasoluja.",
    },
    {
        code: "mcv",
        label: "Punasolujen keskitilavuus (MCV)",
        unit: "fl",
        description: "Punasolujen keskitilavuus (MCV) kertoo yksittäisen punasolun keskimääräisen koon.",
    },
    {
        code: "mch",
        label: "Punasolun keskimääräinen hemoglobiinimäärä (MCH)",
        unit: "pg",
        description: "Kertoo, kuinka paljon hemoglobiinia yhdessä punasolussa keskimäärin on.",
    },
    {
        code: "rdw",
        label: "Punasolujen kokojakauma (RDW)",
        unit: "%",
        description: "Punasolujen kokojakauma (RDW) kertoo, kuinka paljon punasolujen koko vaihtelee keskenään.",
    },
    {
        code: "leuk",
        label: "Leukosyytit",
        unit: "10⁹/L",
        description: "Leukosyytit eli valkosolut ovat osa elimistön puolustusjärjestelmää.",
    },
    {
        code: "trom",
        label: "Trombosyytit",
        unit: "10⁹/L",
        description: "Trombosyytit eli verihiutaleet osallistuvat veren hyytymiseen.",
    },
];
// Derived from LAB_ANALYTES rather than written out a second time, so the
// enum cannot drift from the catalog.
export const labAnalyteCodeSchema = z.enum(LAB_ANALYTES.map((entry) => entry.code));
// The three the client can type in by hand. A strict subset: a client
// entering a full PVK by hand is not a real flow, but the catalog above must
// carry all 11 for the partner-lab path and for rendering a full panel.
export const SELF_REPORTABLE_ANALYTES = ["ferritin", "crp", "hemoglobin"];
// The units a client may enter each self-reportable analyte in, per market,
// the first being the default. The unit is stored exactly as entered and is
// never converted; this is also what the server validates against. Finland is
// the catalog unit alone. The US list is provisional, pending Mari.
export const SELF_REPORT_UNITS = {
    fi: {
        ferritin: ["µg/L"],
        crp: ["mg/L"],
        hemoglobin: ["g/L"],
    },
    us: {
        ferritin: ["ng/mL"],
        crp: ["mg/L"],
        hemoglobin: ["g/dL"],
    },
};
// PVK sub-panel display order, matching the official B-PVKT sub-panel
// grouping (product/prototypes/client-journey.md).
export const PVK_ANALYTE_ORDER = [
    "hemoglobin",
    "eryt",
    "hkr",
    "mcv",
    "mch",
    "rdw",
    "leuk",
    "trom",
];
// The only lab we can currently assert a result came from. All webstore
// (Ferrivia-ordered) draws are processed by Arvoterveys today — a structural
// fact of the current commerce setup, not an inference. A second code is
// added here, not invented, the day a second partner lab exists.
export const LAB_CODES = ["arvo_terveys"];
export const labCodeSchema = z.enum([...LAB_CODES]);
export const DEFAULT_LAB_CODE = "arvo_terveys";
export const labSexSchema = z.enum(["male", "female"]);
// Reference ranges: keyed by (lab, analyte, sex), never joined onto a result
// row. `refText` is an opaque display string ("30–150", "<3") rather than a
// refLow/refHigh pair — that is what makes it structurally impossible for any
// caller to compare a measured value to it.
// Sourced from Vita Labs, the partner lab processing all Arvoterveys draws
// (2026-09-04 handoff from Ville — Vita Labs' own reference sheet).
export const LAB_REFERENCE_RANGES = [
    { labCode: "arvo_terveys", analyteCode: "ferritin", sex: "female", refText: "15–150" },
    { labCode: "arvo_terveys", analyteCode: "ferritin", sex: "male", refText: "30–400" },
    { labCode: "arvo_terveys", analyteCode: "crp", sex: "female", refText: "0.05–3" },
    { labCode: "arvo_terveys", analyteCode: "crp", sex: "male", refText: "0.05–2.5" },
    { labCode: "arvo_terveys", analyteCode: "hemoglobin", sex: "female", refText: "117–155" },
    { labCode: "arvo_terveys", analyteCode: "hemoglobin", sex: "male", refText: "134–167" },
    { labCode: "arvo_terveys", analyteCode: "eryt", sex: "female", refText: "3.90–5.20" },
    { labCode: "arvo_terveys", analyteCode: "eryt", sex: "male", refText: "4.25–5.70" },
    { labCode: "arvo_terveys", analyteCode: "hkr", sex: "female", refText: "35–46" },
    { labCode: "arvo_terveys", analyteCode: "hkr", sex: "male", refText: "39–50" },
    { labCode: "arvo_terveys", analyteCode: "mcv", sex: "female", refText: "82–98" },
    { labCode: "arvo_terveys", analyteCode: "mcv", sex: "male", refText: "82–98" },
    { labCode: "arvo_terveys", analyteCode: "mch", sex: "female", refText: "27–33" },
    { labCode: "arvo_terveys", analyteCode: "mch", sex: "male", refText: "27–33" },
    { labCode: "arvo_terveys", analyteCode: "rdw", sex: "female", refText: "<15" },
    { labCode: "arvo_terveys", analyteCode: "rdw", sex: "male", refText: "<14" },
    { labCode: "arvo_terveys", analyteCode: "leuk", sex: "female", refText: "3.4–8.2" },
    { labCode: "arvo_terveys", analyteCode: "leuk", sex: "male", refText: "3.4–8.2" },
    { labCode: "arvo_terveys", analyteCode: "trom", sex: "female", refText: "150–360" },
    { labCode: "arvo_terveys", analyteCode: "trom", sex: "male", refText: "150–360" },
];
// A lookup by three opaque keys, never by the measured value. `sex` selects
// which string to show; it must never be derived from, or feed into, a
// comparison against a value_numeric column.
export function getReferenceRange(labCode, analyteCode, sex) {
    return (LAB_REFERENCE_RANGES.find((entry) => entry.labCode === labCode && entry.analyteCode === analyteCode && entry.sex === sex)?.refText ?? null);
}
/* The self-report write path: a client typing in a lab report done outside
 * Ferrivia. One request is one lab report — a shared collection date and lab
 * name, and an array of analytes read off it. `clientResultId` keys the whole
 * submission, the same sender-generated idempotency contract as
 * `clientCheckinId` and `clientMessageId`. See platform/docs/lab-results.md.
 */
const clientResultIdSchema = z.string().min(1).max(64);
export const selfReportedResultRequestSchema = z.object({
    clientResultId: clientResultIdSchema,
    collectedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    sourceLab: z.string().max(200).nullable(),
    reportFileId: z.string().uuid().optional(),
    analytes: z
        .array(z.object({
        analyteCode: z.enum(SELF_REPORTABLE_ANALYTES),
        value: z.number().finite().positive(),
        unit: z.string().min(1).max(16).optional(),
    }))
        .min(1)
        .max(SELF_REPORTABLE_ANALYTES.length)
        .superRefine((entries, ctx) => {
        const seen = new Set();
        for (const [i, entry] of entries.entries()) {
            if (seen.has(entry.analyteCode)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "analyteCode"],
                    message: "analyteCode repeated within one batch",
                });
            }
            seen.add(entry.analyteCode);
        }
    }),
});
// The created (or merged-into) result, echoed back. Deliberately thin —
// confirms the write, nothing more. No value, no unit, no analyte identity:
// same reasoning as symptoms.ts's symptomCheckinSchema, this feature is
// write-only on the client side.
export const selfReportedResultSchema = z.object({
    id: z.string(),
    collectedOn: z.string(),
    createdAt: z.string(),
});
/* The read path: the Results tab's `GET /labs/results`. `refText` is the same
 * opaque display string `getReferenceRange` returns — a lookup result, never
 * a value the client could compare against. `analyteCode` is left as a plain
 * string rather than `labAnalyteCodeSchema`'s enum, the same lenient-read
 * precedent as `readHomeBlockSchema` in home.ts: a code added to the catalog
 * by a newer server must render as its raw string on an old PWA holding a
 * stale cache, not throw.
 */
export const readLabResultAnalyteSchema = z.object({
    analyteCode: z.string(),
    value: z.number(),
    unit: z.string(),
    refText: z.string().nullable(),
});
// `labCode` is null exactly when the result is self-reported (see the
// store's resultDetail) — the client uses that, not `provenance`, to decide
// whether the range may be labelled with a lab's name (Arvoterveys) or must
// stay a bare, unattributed "Viitearvo".
export const readLabResultSchema = z.object({
    id: z.string(),
    provenance: z.string(),
    labCode: z.string().nullable(),
    sourceLab: z.string().nullable(),
    collectedOn: z.string(),
    reportedAt: z.string(),
    analytes: z.array(readLabResultAnalyteSchema),
});
/* What a purchased panel puts on the laboratory requisition.
 *
 * Keyed by the `lab_panel_code` a product carries in its Stripe metadata
 * (platform/docs/commerce.md) — the same code the ingest path resolves a
 * submission's panel by (apps/api/src/modules/labs/ingest/resolve.ts).
 *
 * This exists so the order confirmation email can tell a client at the draw
 * desk which measurements are being taken. It has to live in reviewed source
 * rather than be read out of the catalog: `lab_panel_code` is set by whoever
 * edits a Stripe product, and the words that reach a client about a
 * laboratory test are not an operations decision (the same reasoning as
 * apps/client's labAddOns.ts).
 *
 * **Names only.** A row here says which measurement is drawn and stops — no
 * values, no ranges, no reason a client might want one (root CLAUDE.md, "The
 * software does not interpret"; REQ-MDR-002). Naming a measurement is not
 * interpreting one, and stating what a measurement is *for* would be an
 * intended-purpose claim under MDR Art. 2(12).
 *
 * PVK is one row, not its eight constituent analytes: the desk is checking a
 * requisition, and `B-PVKT` is drawn and ordered as a single panel. The
 * labels are the ones the results come back in — `LAB_ANALYTES` for the two
 * singles, and the same "Perusverenkuva (PVK)" the Results tab's accordion
 * uses.
 */
export const PANEL_MEASUREMENT_LABELS = {
    ferritin_crp: ["Ferritiini", "Herkkä CRP"],
    pvk: ["Perusverenkuva (PVK)"],
    // The wellbeing-analysis product's "add labs" toggle sells this panel as
    // one Stripe product with one lab_panel_code, rather than as the two
    // separate ferritin_crp + pvk line items the standalone panel purchase
    // uses -- same measurements, single combined code.
    ferritin_crp_pvk: ["Ferritiini", "Herkkä CRP", "Perusverenkuva (PVK)"],
};
/** The measurements an order draws, in catalog order, deduplicated.
 *
 * An unknown or absent panel code contributes nothing rather than guessing —
 * the membership bundle carries no `lab_panel_code` today, so an order can
 * legitimately need a draw booked (`carriesLabMeasurements`) without this
 * being able to name what is drawn. Callers must handle the empty array by
 * omitting the list, never by substituting a default panel. */
export function panelMeasurementLabels(panelCodes) {
    const labels = panelCodes.flatMap((code) => (code == null ? [] : (PANEL_MEASUREMENT_LABELS[code] ?? [])));
    return [...new Set(labels)];
}
/* A lab report PDF a US client uploaded. The bytes are stored and shown as
 * they arrived, never read by the software; the wire shape carries no original
 * filename, because the platform never keeps one. See
 * platform/docs/lab-report-files.md.
 */
export const LAB_REPORT_MAX_BYTES = 10 * 1024 * 1024;
export const labReportFileSchema = z.object({
    id: z.string().uuid(),
    collectedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    uploadedAt: z.string(),
    byteSize: z.number().int().positive(),
    linkedToResult: z.boolean(),
});
